import { z } from 'zod';
import { paginationSchema } from './api.js';
import { LEAD_SOURCES, LEAD_STAGES, TEMPERATURES, } from './enums.js';
/**
 * Indian mobile numbers, permissively accepted and strictly stored.
 * The API normalises to E.164 on every write via @gbv/utils normalisePhone().
 */
const phoneInput = z
    .string()
    .trim()
    .min(10, 'A phone number needs at least 10 digits.')
    .max(20, 'That phone number is too long.')
    .regex(/^[+0-9][0-9\s\-()]*$/, 'Use digits, spaces, or dashes only.');
export const leadCreateSchema = z.object({
    name: z.string().trim().min(2, 'Enter the contact name.').max(120),
    phone: phoneInput,
    altPhone: phoneInput.optional().or(z.literal('')),
    email: z.string().trim().toLowerCase().email('Enter a valid email.').optional().or(z.literal('')),
    company: z.string().trim().max(160).optional(),
    city: z.string().trim().max(80).optional(),
    source: z.enum(LEAD_SOURCES).default('OTHER'),
    stage: z.enum(LEAD_STAGES).default('NEW'),
    temperature: z.enum(TEMPERATURES).default('COLD'),
    valuePaise: z.number().int().min(0).max(1_000_000_00_000).optional(),
    ownerId: z.string().cuid().optional(),
    nextFollowUpAt: z.coerce.date().optional(),
    customFields: z.record(z.unknown()).optional(),
});
export const leadUpdateSchema = leadCreateSchema
    .partial()
    .extend({
    lostReason: z.string().trim().max(400).optional(),
})
    .refine((v) => v.stage !== 'LOST' || Boolean(v.lostReason && v.lostReason.length > 0), {
    path: ['lostReason'],
    message: 'Tell us why this lead was lost — it is the most useful field we collect.',
});
export const leadListQuerySchema = paginationSchema.extend({
    search: z.string().trim().max(120).optional(),
    stage: z.enum(LEAD_STAGES).optional(),
    temperature: z.enum(TEMPERATURES).optional(),
    source: z.enum(LEAD_SOURCES).optional(),
    ownerId: z.string().optional(),
    /**
     * Every lead mirrored onto one account or one contact.
     *
     * Filters on the mirror's links rather than the free-text company name, so a
     * company page shows the enquiries that actually belong to that account —
     * including ones typed as "Sharma Motors Pvt Ltd" against an account called
     * "Sharma Motors".
     */
    companyId: z.string().cuid().optional(),
    personId: z.string().cuid().optional(),
    city: z.string().trim().max(80).optional(),
    createdFrom: z.coerce.date().optional(),
    createdTo: z.coerce.date().optional(),
    followUpFrom: z.coerce.date().optional(),
    followUpTo: z.coerce.date().optional(),
    /** Saved views resolve to preset filter combinations server-side. */
    view: z
        .enum(['my_followups_today', 'hot', 'unassigned', 'new_this_week', 'all'])
        .optional(),
});
export const STAGE_LABELS = {
    NEW: 'New',
    CONTACTED: 'Contacted',
    QUALIFIED: 'Qualified',
    PROPOSAL: 'Proposal',
    NEGOTIATION: 'Negotiation',
    WON: 'Won',
    LOST: 'Lost',
};
export const SOURCE_LABELS = {
    WEBSITE: 'Website',
    REFERRAL: 'Referral',
    WALK_IN: 'Walk-in',
    IMPORT: 'Imported',
    META_ADS: 'Meta Ads',
    GOOGLE_ADS: 'Google Ads',
    WHATSAPP: 'WhatsApp',
    PHONE_IN: 'Inbound call',
    OTHER: 'Other',
};
//# sourceMappingURL=lead.schema.js.map